import styled from '@emotion/styled';
import {lighten} from '@nfq/colors';
import {Col, Container, media, Row, Spacer, spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';
import {AnimatePresence} from 'motion/react';
import * as motion from 'motion/react-m';

import {Slider} from 'UI/components/action/Slider';
import {PayloadImage} from 'UI/components/layout/PayloadImage';
import {P} from 'UI/components/layout/Text';

import {PointerTransition} from 'UI/animations/Pointer';
import {useUspModule} from 'UI/modules/Blocks/UspModule/useUspModule';

import {transient} from 'Application/configs/emotionTransientProps';
import {isDefinedAndPopulated} from 'GlobalShared/utils/typeGuards';

import type {USPModuleBlock} from 'types/payload-types';

const DEFAULT_AUTOPLAY_INTERVAL = 5000;
const PERCENTAGE = 100;

/**
 * The `ComponentProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface ComponentProps {
    /**
     * The interval in ms for the autoplay of the Embla slider.
     */
    autoplayInterval?: USPModuleBlock['autoplayInterval'];
    /**
     * The product image object with `alt` text and `url`.
     */
    image?: USPModuleBlock['image'];
    /**
     * The `points` property is an array of objects that represent interactive points in the `UspModule` component.
     * Each point corresponds to a slide in the module and contains information for its position, identifier, and textual content.
     */
    points?: USPModuleBlock['points'];
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'UspModule'
     */
    testId?: string;
    /**
     * The theme data attribute applied to the component for theming purposes.
     */
    theme?: USPModuleBlock['theme'];
}

/**
 * The `UspModule` component is a React component that renders a visual module showcasing product information and associated points.
 * The component is highly configurable and supports dynamic rendering of product images, interactive points, and a text slider.
 * The image section displays the product image and highlights a point corresponding to the currently selected slide.
 * The slider section displays the textual content (`headline` and `body`) of all points, enabling users to browse through the slides.
 *
 * @param props                  The component props.
 * @param props.autoplayInterval The interval in ms for the autoplay of the Embla slider.
 * @param props.points           An array of points, each containing a `position` (x, y) and slide content (`headline`, `body`).
 * @param props.image            The product image details, including `alt`, `height`, `url`, and `width`. Default is an empty object.
 * @param props.testId           A unique identifier for testing purposes, defaulting to `'UspModule'`.
 * @param props.theme            The theme for the module, allowing style customization.
 * @returns A JSX element displaying the image, interactive points, and a slider with textual content.
 *
 * @example
 * ```tsx
 * const points = [
 *   { id: '1', position: { x: 50, y: 50 }, headline: 'Feature 1', body: 'Description of feature 1.' },
 *   { id: '2', position: { x: 75, y: 75 }, headline: 'Feature 2', body: 'Description of feature 2.' }
 * ];
 *
 * <UspModule
 *   autoplayInterval=5000
 *   points={points}
 *   productImage={{ alt: 'Product Image', height: 400, url: 'image.jpg', width: 600 }}
 *   theme="dark"
 * />
 * ```
 */
const UspModule = observer(({
    autoplayInterval,
    image,
    points,
    testId = 'UspModule',
    theme
}: ComponentProps) => {
    const {activeSlideIndex, aspectRatio, setActiveSlideIndex} = useUspModule(image);

    return (
        <Wrapper
            data-cy={testId}
            data-nfq-theme={theme}
        >
            <Container>
                <Row>
                    <Col justify="center" lg={7} xs={12}>
                        <ImageWrapper>
                            <HeroImageWrapper $aspectRatio={aspectRatio}>
                                {isDefinedAndPopulated(image?.image) && (
                                    <>
                                        <Image image={image} />
                                        <AnimatePresence initial={false} mode="popLayout">
                                            {points?.map(({id, position}, index) => (
                                                index === activeSlideIndex && position.x && position.y
                                                    ? (
                                                        <Pointer
                                                            key={id}
                                                            $x={position.x}
                                                            $y={position.y}
                                                            animate={index === activeSlideIndex ? 'visible' : 'hidden'}
                                                            exit="exit"
                                                            initial="hidden"
                                                            variants={PointerTransition}
                                                        />
                                                    )
                                                    : null
                                            ))}
                                        </AnimatePresence>
                                    </>
                                )}
                            </HeroImageWrapper>
                        </ImageWrapper>
                    </Col>
                    <Col justify="center" lg={5} xs={12} xxl={4}>
                        {points && (
                            <Slider
                                autoplayInterval={autoplayInterval ?? DEFAULT_AUTOPLAY_INTERVAL}
                                data-cy={testId}
                                setActiveSlideIndex={setActiveSlideIndex}
                                sliderOptions={{
                                    loop: true,
                                    slidesToScroll: 1
                                }}
                                variant="usp"
                                autoplay
                                overflowHidden
                            >
                                {points.map(slide => (
                                    <Slide key={slide.id}>
                                        <SlideHeadline>{slide.headline}</SlideHeadline>
                                        <Spacer y={6} />
                                        <P>{slide.body}</P>
                                    </Slide>
                                ))}
                            </Slider>
                        )}
                    </Col>
                </Row>
            </Container>
        </Wrapper>
    );
});

UspModule.displayName = 'UspModule';

export {UspModule};

const Wrapper = styled.div`
    background-color: ${({theme}) => theme.colors.bgPrimary};
    padding-block: ${spacing(14)};

    ${media('md')} {
        padding-block: ${spacing(30)};
    }
`;

const ImageWrapper = styled.div`
    align-items: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    text-align: center;
`;

interface HeroImageWrapperProps {
    $aspectRatio: string;
}

const HeroImageWrapper = styled.div<HeroImageWrapperProps>`
    aspect-ratio: ${({$aspectRatio}) => $aspectRatio};
    max-width: 100%;
    position: relative;
    width: 100%;
`;

const Image = styled(PayloadImage)`
    height: 100%;
    object-fit: cover;
    width: 100%;
`;

interface PointerProps {
    $x: number;
    $y: number;
}

const Pointer = styled(motion.div, {...transient}) <PointerProps>`
    background-color: ${({theme}) => (lighten(theme.colors.aceSecondary, 40))};
    border-radius: 50%;
    height: 3.2rem;
    left: ${({$x}) => `${$x * PERCENTAGE}%`};
    position: absolute;
    top: ${({$y}) => `${$y * PERCENTAGE}%`};
    width: 3.2rem;

    ${media('md')} {
        height: 4rem;
        width: 4rem;
    }
`;

const Slide = styled.div`
    color:  ${({theme}) => theme.colors.secondary};
    min-width: 15rem;
    text-align: center;

    ${media('md')} {
        max-width: 58rem;
        padding: 0 0.5rem;
        text-align: start;
    }
`;

const SlideHeadline = styled.div`
    color:  ${({theme}) => theme.colors.primary};
    font-size: 3.6rem;
    hyphens: auto;
    line-height: 1.2;
    text-wrap: pretty;

    ${media('md')} {
        font-size: 6.4rem;
    }
`;